/**
 * A page loaded from the previous build calling into the new one.
 *
 * Every release replaces prod-web (Cloud Run blue-green). A tab opened
 * before it still holds the old build: its Server Action ids and JS
 * chunk names no longer exist on the server. Next answers the action with
 * `UnrecognizedActionError` ("Server Action "…" was not found on the
 * server"), and a lazy import of a vanished chunk fails to load. Neither
 * is a bug in the page — the fix is to load the new build. Owner report
 * 2026-09-29: sending to Zero a minute after v1.166.0 went live showed
 * the raw Next error and lost the typed message.
 *
 * `NEXT_DEPLOYMENT_ID` (next.config `deploymentId`) makes navigations
 * hard-reload across a skew; an action call or a chunk load in place does
 * not, so the two surfaces that see it — the route error boundary and the
 * thread composer — ask here.
 */
import { unstable_isUnrecognizedActionError } from 'next/navigation';

const ACTION_NOT_FOUND = /Server Action ".+" was not found on the server/i;
const CHUNK_GONE =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i;

export function isDeploySkewError(error: unknown): boolean {
  if (unstable_isUnrecognizedActionError(error)) return true;
  if (!(error instanceof Error)) return false;
  return error.name === 'ChunkLoadError' || ACTION_NOT_FOUND.test(error.message) || CHUNK_GONE.test(error.message);
}

/** One automatic reload per this window — a second skew right after means reloading does not fix it. */
export const SKEW_RELOAD_WINDOW_MS = 30_000;
const SKEW_RELOAD_KEY = 'connect0:deploy-skew-reload-at';

/**
 * Reloads the page onto the current build unless it already did so in the
 * last `SKEW_RELOAD_WINDOW_MS` (then returns false and the caller shows a
 * manual Reload). Without session storage (blocked, private mode) the
 * guard cannot tell a loop from a first try, so it does not reload
 * automatically either.
 */
export function reloadOnceForSkew(now: number = Date.now(), win: Window = window): boolean {
  try {
    const last = Number(win.sessionStorage.getItem(SKEW_RELOAD_KEY) ?? '0');
    if (Number.isFinite(last) && now - last < SKEW_RELOAD_WINDOW_MS) return false;
    win.sessionStorage.setItem(SKEW_RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  win.location.reload();
  return true;
}
